import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { EQimaSnackbarType } from '@qima/ngx-qima';
import { StatusCodes } from 'http-status-codes';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppSnackbarService } from '../../services/app-snackbar.service';
import { EApiBusinessErrorCode } from '../models/enums/api-business-error-code.enum';

/**
 * List of URL patterns to exclude from error page redirection
 * These are typically authentication-related endpoints, i18n files, or assets
 */
const EXCLUDED_URL_PATTERNS = ['/assets/', '/auth/v2/token'];

/**
 * API error body key to snackbar translation key.
 * Add a row when a new business error should be shown to the user.
 */
const BUSINESS_ERROR_SNACKBAR_KEYS: Readonly<Record<EApiBusinessErrorCode, string>> = {
  [EApiBusinessErrorCode.AUDIT_PACK_REQUIRED_DOCUMENT_STATUS_NOT_UPLOADED_BY_AUDITOR]:
    'AUDIT_PACK_NOTIFICATION.AUDIT_PACK_REQUIRED_DOCUMENT_STATUS_NOT_UPLOADED_BY_AUDITOR',
  [EApiBusinessErrorCode.SALES_DOCUMENTS_NOT_READY_FOR_CONFIRMATION]: 'BUSINESS_ERROR.SALES_DOCUMENTS_NOT_READY_FOR_CONFIRMATION',
};

/**
 * Configuration for skipping error page redirection based on request method and URL pattern
 */
interface ISkipErrorRedirectRule {
  method: string;
  urlPattern: string;
  statusCode?: number; // Optional status code to match. If provided, only matches when error status matches
}

/**
 * File-related rules to skip error page redirection
 * Rules for file upload, download, and file management endpoints
 */
const FILE_RELATED_SKIP_RULES: ISkipErrorRedirectRule[] = [
  { method: 'POST', urlPattern: '/file/', statusCode: StatusCodes.REQUEST_TOO_LONG },
  { method: 'POST', urlPattern: '/upload-cpv', statusCode: StatusCodes.REQUEST_TOO_LONG },
  // Add more file-related rules here as needed
];

/**
 * List of rules to skip error page redirection
 * Each rule matches a specific HTTP method and URL pattern
 */
const SKIP_ERROR_REDIRECT_RULES: ISkipErrorRedirectRule[] = [
  ...FILE_RELATED_SKIP_RULES,
  // Add more rules here as needed
];

/**
 * Check if a rule matches the request and error
 */
function matchesRule(rule: ISkipErrorRedirectRule, request: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
  return (
    rule.method === request.method &&
    request.url.includes(rule.urlPattern) &&
    (rule.statusCode === undefined || rule.statusCode === error.status)
  );
}

/**
 * HTTP error interceptor that handles server-side errors (403, 500, etc.)
 * and redirects to the appropriate error page
 */
export function httpErrorInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const router = inject(Router);
  const ngZone = inject(NgZone);
  const appSnackbarService = inject(AppSnackbarService);

  return next(request).pipe(
    catchError((error: unknown) => {
      // Only handle HttpErrorResponse
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      showBusinessErrorSnackbar(error, appSnackbarService);

      // Skip redirection based on method and URL pattern rules
      const isFileRelatedRule = FILE_RELATED_SKIP_RULES.some(rule => matchesRule(rule, request, error));

      const shouldSkipRedirect = SKIP_ERROR_REDIRECT_RULES.some(rule => matchesRule(rule, request, error));
      if (shouldSkipRedirect) {
        if (isFileRelatedRule) {
          appSnackbarService.showSnackbar('COMMON.UPLOAD_ERROR_FILE_SIZE', EQimaSnackbarType.ERROR);
          return EMPTY;
        }
        return throwError(() => error);
      }

      // Skip redirection for excluded URLs (assets and login API)
      const isExcluded = EXCLUDED_URL_PATTERNS.some(pattern => request.url.includes(pattern));
      if (isExcluded) {
        return throwError(() => error);
      }

      // Skip redirection if already on error page to prevent infinite loops
      if (router.url.startsWith('/error')) {
        return throwError(() => error);
      }

      // Handle specific HTTP error codes
      switch (error.status) {
        case StatusCodes.FORBIDDEN.valueOf():
          ngZone.run(() => {
            void router.navigate(['/error'], {
              queryParams: { code: StatusCodes.FORBIDDEN.valueOf() },
              replaceUrl: true,
            });
          });
          return EMPTY;
        case StatusCodes.NOT_IMPLEMENTED.valueOf():
        case StatusCodes.SERVICE_UNAVAILABLE.valueOf():
        case StatusCodes.BAD_GATEWAY.valueOf():
          ngZone.run(() => {
            void router.navigate(['/error'], {
              queryParams: { code: StatusCodes.INTERNAL_SERVER_ERROR.valueOf() },
              replaceUrl: true,
            });
          });
          return EMPTY;
        default:
          break;
      }

      return throwError(() => error);
    })
  );
}

function showBusinessErrorSnackbar(error: HttpErrorResponse, appSnackbarService: AppSnackbarService): void {
  if (error.status !== StatusCodes.BAD_REQUEST.valueOf()) {
    return;
  }

  const messageKey = BUSINESS_ERROR_SNACKBAR_KEYS[readErrorKey(error) as EApiBusinessErrorCode];
  if (messageKey === undefined) {
    return;
  }

  appSnackbarService.showSnackbar(messageKey, EQimaSnackbarType.ERROR);
}

function readErrorKey(error: HttpErrorResponse): string {
  const payload: unknown = error.error;
  if (typeof payload === 'string') {
    return payload.trim();
  }

  if (payload !== null && typeof payload === 'object' && 'text' in payload) {
    const text = (payload as { text?: unknown }).text;
    return typeof text === 'string' ? text.trim() : '';
  }

  return '';
}
